import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

export type BackHandler = () => boolean;

interface BackHandlerContextType {
  registerBackHandler: (handler: BackHandler) => () => void;
  invokeBackHandler: () => boolean;
}

const BackHandlerContext = createContext<BackHandlerContextType | undefined>(
  undefined,
);

interface BackHandlerProviderProps {
  children: ReactNode;
}

export const BackHandlerProvider: React.FC<BackHandlerProviderProps> = ({
  children,
}) => {
  const handlersRef = useRef<BackHandler[]>([]);

  const registerBackHandler = useCallback((handler: BackHandler) => {
    handlersRef.current.push(handler);

    return () => {
      handlersRef.current = handlersRef.current.filter((h) => h !== handler);
    };
  }, []);

  const invokeBackHandler = useCallback(() => {
    for (let i = handlersRef.current.length - 1; i >= 0; i -= 1) {
      if (handlersRef.current[i]()) {
        return true;
      }
    }

    return false;
  }, []);

  return (
    <BackHandlerContext.Provider
      value={{ registerBackHandler, invokeBackHandler }}
    >
      {children}
    </BackHandlerContext.Provider>
  );
};

export const useBackHandler = (): BackHandlerContextType => {
  const context = useContext(BackHandlerContext);

  if (!context) {
    throw new Error('useBackHandler must be used within BackHandlerProvider');
  }

  return context;
};

export const useRegisterBackHandler = (
  handler: BackHandler,
  enabled = true,
): void => {
  const { registerBackHandler } = useBackHandler();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return registerBackHandler(handler);
  }, [enabled, handler, registerBackHandler]);
};
